import * as React from 'react';
import * as update from 'immutability-helper';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Col, Row } from 'antd';
import Button from 'common/components/Button';
import Field from './Field';
import asyncRuleFactory from './utils/ruleFactory';
import { Config, Options } from './types';
import { languages } from 'common/i18n';
import dynamicFC from './utils/dynamicFC';
const FormItem = Form.Item;

export { dynamicFC };

export default (options?: Options): any => {
    interface iProps {
        config: Config;
        extension?: (form: any) => void;
        onFieldsChange?: (fields: any) => void;
        onValuesChange?: (values: any) => void;
        onSubmit?: (values: any) => void;
        form?: any;
        status?: any;
        security?: any;
    }

    @(Form.create({
        onFieldsChange: ({ onFieldsChange }: any, fields: any) => {
            onFieldsChange && onFieldsChange(fields);
        },
        onValuesChange: ({ onValuesChange }: any, values: any) => {
            onValuesChange && onValuesChange(values);
        }
    }) as any)
    class FormInstance extends React.Component<iProps> {
        /**
         * 点击事件处理
         * @param validate
         * @param callback
         */
        clickHandler = (validate: boolean, callback: Function) => (e: any) => {
            e.preventDefault();
            validate ? this.props.form.validateFields(callback) : callback();
        };

        /**
         * 获取布局属性
         * @param layout
         * @param customize
         */
        getLayout = (layout: string, customize?: { [key: string]: Array<object> }) =>
            ({
                'horizontal': {
                    itemLayout: {
                        labelCol: { xl: 6, md: 8 },
                        wrapperCol: { xl: 18, md: 16 },
                        colon: false
                    },
                    buttonLayout: {
                        wrapperCol: { span: 20 }
                    }
                }
            } as any)[layout] || {
                itemLayout: customize || {},
                buttonLayout: {}
            };

        /**
         * 获取输入域元素
         */
        getFields = () => {
            const { Rules } = options;
            const { config, form } = this.props;
            const { layout = 'vertical', columns, lock, items, itemClassName, itemLayoutCustomize = {} } = config;
            const { itemLayout } = this.getLayout(layout, itemLayoutCustomize);
            const { getFieldDecorator } = form;
            return items
                .filter(item => {
                    return item;
                })
                .map((item: any) => {
                    return {
                        ...item,
                        lang: languages.getDefault(),
                        disabled: item.disabled !== undefined ? item.disabled : lock
                    };
                })
                .reduce((prev: any, item: any, index: number) => {
                    const { width, description, label, key, validators, custom, disabled, exclusiveLine, fullWidth, staticView, defaultValue, ignore } = item;

                    // 校验规则
                    const rules = validators
                        ? validators.filter((item: any) => !!item).map((item: any) => {
                            if (Array.isArray(item)) {
                                const [ruleName, ruleParams, callback] = item;
                                const rule = Rules.get(ruleName);
                                return typeof rule === 'function'
                                    ? rule(ruleParams)
                                    : asyncRuleFactory(rule, { defaultValue, ...ruleParams }, callback);
                            } else if (typeof (item) === "object") {
                                const { validator, message } = item;
                                return {
                                    validator: (rule: any, value: any, callback: Function) => {
                                        validator(rule, value, callback, { form, key });
                                    },
                                    message
                                };
                            } else {
                                if (!ignore) {
                                    let rule = Rules.get(item);
                                    if (rule) {
                                        const { validator, message } = rule;
                                        return {
                                            ...rule,
                                            validator: validator
                                                ? (rule: any, value: any, callback: Function) => {
                                                    validator(rule, value, callback, { form, key });
                                                }
                                                : validator,
                                            message
                                        };
                                    }
                                }
                            }
                        }).filter((item: any) => !!item)
                        : [];
                    // 输入域元素包装器
                    const decorator = (options: object = {}) => getFieldDecorator(key, {
                        initialValue: item.defaultValue,
                        validateTrigger: 'onBlur',
                        rules,
                        validateFirst: true,
                        ...options
                    });
                    // 表单项label
                    const isRequire: boolean = !!validators && validators.join('.').indexOf('required') > -1;
                    const ItemLabel: any = (() => {
                        return label ? <span title={label} className="ellipsis">{label}</span> : '';
                    })();
                    // 输入域元素
                    const getFormItem: any = () => [
                        description &&
                        <label key={`${key}_description`} className={ItemLabel}>{description}</label>,
                        <FormItem key={key} label={ItemLabel} {...itemLayout} required={!(disabled && staticView) && isRequire}
                            className={itemClassName || `Wireframe ${item.itemClassName || ''}`}>
                            {custom
                                ? typeof (custom) === 'function'
                                    ? custom(form, decorator)
                                    : React.cloneElement(custom, { form, decorator })
                                : Field(item, decorator)}
                        </FormItem>
                    ];
                    if (columns) {
                        const cols = prev[2] + (width || 1);
                        const pending = prev[1].concat([
                            <Col key={`v4-col-${index}`}
                                span={exclusiveLine && fullWidth ? 24 : 24 * (width || 1) / columns} className={'first'}>{getFormItem()}</Col>
                        ]);
                        return exclusiveLine || cols >= columns
                            ? [update(prev[0], { $push: [pending] }), [], 0]
                            : [prev[0], pending, cols];
                    } else {
                        return [prev[0].concat(getFormItem()), prev[1], 0];
                    }
                }, [[], [], 0])
                .reduce((prev: any, curr: any, index: number) => update(prev, { $push: index < 2 ? curr : [] }));
        };

        /**
         * 获取按钮元素
         */
        getButtons = () => {
            const { config } = this.props;
            const { layout = 'vertical', lock, buttons } = config;
            const { buttonLayout } = this.getLayout(layout);
            return <FormItem style={{ marginBottom: 0 }} {...buttonLayout}>
                <div className="buttom-wrapper">{buttons.map((item: any, index: number) =>
                    <Button {...{
                        key: index,
                        type: item.color,
                        htmlType: item.type,
                        style: item.style,
                        size: item.size || 'default',
                        onClick: item.callback && this.clickHandler(item.validate, item.callback),
                        disabled: (item.type === 'submit' && lock) || item.lock
                    }}>{item.text}</Button>)}
                </div>
            </FormItem>;
        };

        render() {
            const { config, extension, onSubmit, form } = this.props;
            const { name, layout = 'vertical', columns, buttons, rowGutter = 110 } = config;
            // 扩展选项
            if (extension) extension(form);
            return (
                <Form className={name} layout={layout}
                    onSubmit={this.clickHandler(true, (err: any, values: any) => {
                        !err && onSubmit && onSubmit(values);
                    })}>
                    {columns
                        ? this.getFields().map((item: any, index: number) => (
                            <Row key={index} gutter={rowGutter}>{item}</Row>
                        ))
                        : this.getFields()
                    }
                    {buttons && (
                        columns
                            ? <Row gutter={rowGutter}>
                                <Col span={24}>{this.getButtons()}</Col>
                            </Row>
                            : this.getButtons()
                    )}
                </Form>
            );
        }
    }

    return FormInstance;
};
